import { FC, memo, useCallback } from "react";

import __ from "web/Layout/Translations";

import { Classes, SimpleFn } from "web/types/Common";

import classify from "web/classify";
import { useAppContext } from "web/context/app";

import { IsThankYouPage, Link, Pathname, Search } from "../../Types/types";
import NavigationItem from "./NavigationItem";
import defaultClasses from "./navigationList.scss";

interface ICheckoutNavigationList {
  links: Link[];
  pathname: Pathname;
  search: Search;
  classes: Classes;
  listItemClasses?: Classes;
  isThankYouPage?: IsThankYouPage;
  onClick?: SimpleFn;
}

const CheckoutNavigationList: FC<ICheckoutNavigationList> = memo(
  ({
    links,
    pathname,
    search,
    classes,
    listItemClasses = {},
    isThankYouPage = false,
    onClick,
  }) => {
    const { isMobile } = useAppContext();

    const { key: activeStepKey } =
      links.find(({ link }) => link === `${pathname}${search}`) || {};

    const onClickHandler = useCallback(() => {
      if (typeof onClick === "function") onClick();
    }, [onClick, activeStepKey]);

    if (!activeStepKey) return null;

    const listItems = links.map(({ link, label, key }, index) => {
      const enabled = isThankYouPage ? false : activeStepKey > key;
      const active = isThankYouPage ? true : activeStepKey === key;

      return (
        <li
          key={key}
          className={classes.item}
          data-t2={link}
          data-t1="navigation_list_item"
          data-testid="navigation_list_item"
          aria-current={
            isMobile && activeStepKey === key ? "step" : undefined
          }
        >
          <NavigationItem
            label={label}
            {...(isMobile
              ? { stepKey: key, showStepNumber: true }
              : null)}
            enabled={enabled}
            active={active}
            link={link}
            classes={listItemClasses}
            isThankYouPageHelper={
              isThankYouPage && links.length - 1 !== index
            }
            onClick={() => onClickHandler()}
          />
        </li>
      );
    });

    if (isMobile) {
      return (
        <ol
          className={classes.nav}
          aria-label={__("Kroki procesu zakupowego")}
        >
          {listItems}
        </ol>
      );
    }

    return <ul className={classes.nav}>{listItems}</ul>;
  }
);

export default classify<ICheckoutNavigationList>(defaultClasses)(
  CheckoutNavigationList
);
